import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db, isFirebaseConfigured } from '../../firebase';
import './Feedback.css';

const TYPE_LABELS = {
  suggestion: 'Suggestion / idea',
  bug: 'Bug or issue',
  general: 'General feedback',
};

function formatDate(isoString) {
  if (!isoString) return '-';
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoString;
  }
}

export default function Feedback() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const firebaseReady = isFirebaseConfigured() && db;

    if (!firebaseReady) {
      setError('Feedback storage is not configured. View submissions in Firebase Console.');
      setLoading(false);
      return;
    }

    async function load() {
      try {
        const q = query(
          collection(db, 'feedback'),
          orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        const list = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setItems(list);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Could not load feedback. You may need to allow read access in Firestore rules.');
        setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="feedback-page">
      <div className="feedback-hero">
        <h1 className="feedback-page-title">Feedback</h1>
        <p className="feedback-page-subtitle">
          All feedback submitted via the Feedback button across the site.
        </p>
      </div>

      <div className="feedback-container">
        {loading && (
          <p className="feedback-status" aria-live="polite">Loading feedback…</p>
        )}

        {error && !loading && (
          <div className="feedback-error" role="alert">
            <p>{error}</p>
            <p className="feedback-error-hint">
              To view feedback here, update Firestore rules to allow read for authenticated users. See FEEDBACK_SETUP.md.
            </p>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <p className="feedback-empty">No feedback submitted yet.</p>
        )}

        {!loading && !error && items.length > 0 && (
          <ul className="feedback-list" aria-label="Feedback submissions">
            {items.map((item) => (
              <li key={item.id} className="feedback-card">
                <div className="feedback-card-header">
                  <span className={`feedback-type feedback-type-${item.type || 'general'}`}>
                    {TYPE_LABELS[item.type] || item.type || 'General feedback'}
                  </span>
                  <time className="feedback-date" dateTime={item.createdAt}>
                    {formatDate(item.createdAt)}
                  </time>
                </div>
                <p className="feedback-message">{item.message}</p>
                <div className="feedback-meta">
                  {item.email && (
                    <span className="feedback-email">{item.email}</span>
                  )}
                  {item.pageUrl && (
                    <a
                      href={item.pageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="feedback-page-link"
                    >
                      View page
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
